import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";

import { reservedGoSlugs } from "../constants/routes";
import { ApiClientError, createApiClient } from "../infrastructure/http/apiClient";
import { brandMarkPath } from "../components/common/BrandLockup";
import {
  createPublicAppointment,
  getPublicBookingContext,
  getPublicGeneralSlots,
  getPublicResourceSlots,
  getPublicResources,
  getPublicServices,
  type CreatePublicAppointmentInput,
  type PublicBookingContext,
  type PublicResource,
  type PublicService,
  type PublicSlot
} from "../modules/publicBooking";
import { NotFoundPage } from "./NotFoundPage";

type BookingPageState =
  | { status: "loading" }
  | { status: "success"; context: PublicBookingContext; services: PublicService[]; resources: PublicResource[] }
  | { status: "not_found" }
  | { status: "error"; retryable: boolean };

type SlotsState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "success"; slots: PublicSlot[] }
  | { status: "error"; retryable: boolean };

type SubmitState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "success"; serviceName: string; resourceName: string; startTime: string; endTime: string; timezone: string }
  | { status: "conflict" }
  | { status: "error"; retryable: boolean };

type BookingFormState = {
  serviceID: string;
  resourceChoice: string;
  date: string;
  slotStartTime: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  customerBirthDate: string;
  saveServicePreference: boolean;
  saveResourcePreference: boolean;
  notes: string;
};

type BookingFormErrors = Partial<Record<keyof BookingFormState, string>>;

const ANY_RESOURCE = "any";
const EXTENDED_FIELD_BIRTH_DATE = "birth_date";
const EXTENDED_FIELD_PREFERRED_SERVICE_ID = "preferred_service_id";
const EXTENDED_FIELD_PREFERRED_RESOURCE_ID = "preferred_resource_id";
const STEP_COUNT = 4;
type BookingStep = 0 | 1 | 2 | 3;
export type ServiceVisual = {
  key: "cut" | "beard" | "combo" | "color" | "nails" | "brow" | "makeup" | "esthetic" | "default";
  label: string;
  tone: "blue" | "gray" | "amber" | "green" | "purple" | "rose";
};
export type BeautyBookingSegment =
  | "barbearia"
  | "cabeleireiro"
  | "manicure"
  | "nail_design"
  | "lash_design"
  | "sobrancelha"
  | "maquiagem"
  | "estetica_beauty";

export type BookingSegmentPreset = {
  segment: BeautyBookingSegment;
  capabilities: string[];
  steps: Array<"service" | "professional" | "schedule" | "customer">;
  themeVariant: "objective" | "visual_reference" | "care_context" | "consent_context";
  referenceRequired: boolean;
  photoPreviewEnabled: boolean;
  upsellEnabled: boolean;
  professionalSelectionMode: "policy_driven";
  copy: {
    serviceIntro: string;
    anyProfessional: string;
    notesPlaceholder: string;
    customerHelp: string;
  };
};

export const beautyBookingSegmentPresets: Record<BeautyBookingSegment, BookingSegmentPreset> = {
  barbearia: {
    segment: "barbearia",
    capabilities: ["short_flow", "professional_choice", "minimal_capture"],
    steps: ["service", "professional", "schedule", "customer"],
    themeVariant: "objective",
    referenceRequired: false,
    photoPreviewEnabled: false,
    upsellEnabled: false,
    professionalSelectionMode: "policy_driven",
    copy: {
      serviceIntro: "Selecione o servico desejado para iniciar sua reserva.",
      anyProfessional: "Visualizar agenda mais proxima",
      notesPlaceholder: "Ex.: degradê baixo, barba marcada, preferencia de acabamento",
      customerHelp: "Voce recebera a confirmacao pelo canal informado."
    }
  },
  cabeleireiro: {
    segment: "cabeleireiro",
    capabilities: ["service_context", "optional_notes", "professional_choice"],
    steps: ["service", "professional", "schedule", "customer"],
    themeVariant: "care_context",
    referenceRequired: false,
    photoPreviewEnabled: false,
    upsellEnabled: true,
    professionalSelectionMode: "policy_driven",
    copy: {
      serviceIntro: "Escolha o atendimento e informe o contexto quando finalizar.",
      anyProfessional: "Mostrar a primeira agenda disponivel",
      notesPlaceholder: "Ex.: comprimento atual, objetivo do corte, quimica recente",
      customerHelp: "Contexto opcional ajuda a equipe a preparar seu atendimento."
    }
  },
  manicure: {
    segment: "manicure",
    capabilities: ["visual_reference", "optional_notes", "upsell_prompt"],
    steps: ["service", "professional", "schedule", "customer"],
    themeVariant: "visual_reference",
    referenceRequired: false,
    photoPreviewEnabled: true,
    upsellEnabled: true,
    professionalSelectionMode: "policy_driven",
    copy: {
      serviceIntro: "Escolha o servico e descreva sua referencia no final.",
      anyProfessional: "Encontrar agenda mais proxima",
      notesPlaceholder: "Ex.: formato, cor, inspiracao ou manutencao",
      customerHelp: "Referencia visual pode ser combinada pelo WhatsApp apos a reserva."
    }
  },
  nail_design: {
    segment: "nail_design",
    capabilities: ["visual_reference", "photo_preview", "upsell_prompt"],
    steps: ["service", "professional", "schedule", "customer"],
    themeVariant: "visual_reference",
    referenceRequired: false,
    photoPreviewEnabled: true,
    upsellEnabled: true,
    professionalSelectionMode: "policy_driven",
    copy: {
      serviceIntro: "Escolha o servico base e deixe a referencia para a preparacao.",
      anyProfessional: "Ver melhor encaixe da equipe",
      notesPlaceholder: "Ex.: nail art, banho de gel, manutencao ou referencia",
      customerHelp: "A equipe pode pedir imagem de referencia no canal de confirmacao."
    }
  },
  lash_design: {
    segment: "lash_design",
    capabilities: ["first_time_context", "maintenance_context", "care_prompt"],
    steps: ["service", "professional", "schedule", "customer"],
    themeVariant: "care_context",
    referenceRequired: false,
    photoPreviewEnabled: false,
    upsellEnabled: false,
    professionalSelectionMode: "policy_driven",
    copy: {
      serviceIntro: "Escolha o procedimento e informe se e primeira vez ou manutencao.",
      anyProfessional: "Ver agenda mais proxima",
      notesPlaceholder: "Ex.: primeira aplicacao, manutencao, sensibilidade ou estilo",
      customerHelp: "Observacoes ajudam a equipe a orientar os cuidados."
    }
  },
  sobrancelha: {
    segment: "sobrancelha",
    capabilities: ["first_time_context", "maintenance_context", "care_prompt"],
    steps: ["service", "professional", "schedule", "customer"],
    themeVariant: "care_context",
    referenceRequired: false,
    photoPreviewEnabled: false,
    upsellEnabled: false,
    professionalSelectionMode: "policy_driven",
    copy: {
      serviceIntro: "Escolha o atendimento e conte se e design novo ou manutencao.",
      anyProfessional: "Encontrar horario mais proximo",
      notesPlaceholder: "Ex.: manutencao, henna, assimetria ou primeira vez",
      customerHelp: "Detalhes opcionais deixam a avaliacao mais precisa."
    }
  },
  maquiagem: {
    segment: "maquiagem",
    capabilities: ["event_context", "optional_notes", "upsell_prompt"],
    steps: ["service", "professional", "schedule", "customer"],
    themeVariant: "care_context",
    referenceRequired: false,
    photoPreviewEnabled: false,
    upsellEnabled: true,
    professionalSelectionMode: "policy_driven",
    copy: {
      serviceIntro: "Escolha o servico e informe o tipo de ocasiao no final.",
      anyProfessional: "Ver melhor horario disponivel",
      notesPlaceholder: "Ex.: casamento, ensaio, pele sensivel ou horario do evento",
      customerHelp: "Contexto da ocasiao ajuda no preparo do atendimento."
    }
  },
  estetica_beauty: {
    segment: "estetica_beauty",
    capabilities: ["consent_context", "optional_notes", "care_prompt"],
    steps: ["service", "professional", "schedule", "customer"],
    themeVariant: "consent_context",
    referenceRequired: false,
    photoPreviewEnabled: false,
    upsellEnabled: false,
    professionalSelectionMode: "policy_driven",
    copy: {
      serviceIntro: "Escolha o procedimento e informe observacoes relevantes.",
      anyProfessional: "Encontrar agenda compativel",
      notesPlaceholder: "Ex.: alergias, gestacao, tratamento recente ou sensibilidade",
      customerHelp: "Observacoes clinicas opcionais ajudam a equipe a confirmar a elegibilidade."
    }
  }
};

export function resolveBeautyBookingSegmentPreset(context: PublicBookingContext): BookingSegmentPreset {
  // The current public contract exposes vertical, not a persisted sub-segment.
  // Until the backend adds an explicit segment field, Beauty uses the default
  // preset while keeping all segment presets centralized in the engine config.
  if (context.tenant.vertical === "beauty") {
    return beautyBookingSegmentPresets.barbearia;
  }
  return beautyBookingSegmentPresets.barbearia;
}

export function BookingPage({ tenantSlug }: { tenantSlug: string }) {
  if (!tenantSlug || tenantSlug.includes("/") || reservedGoSlugs.has(tenantSlug)) {
    return <NotFoundPage />;
  }

  const apiClient = useMemo(() => createApiClient(), []);
  const [state, setState] = useState<BookingPageState>({ status: "loading" });
  const [slotsState, setSlotsState] = useState<SlotsState>({ status: "idle" });
  const [submitState, setSubmitState] = useState<SubmitState>({ status: "idle" });
  const [step, setStep] = useState<BookingStep>(0);
  const [slotsRetryKey, setSlotsRetryKey] = useState(0);
  const [form, setForm] = useState<BookingFormState>({
    serviceID: "",
    resourceChoice: ANY_RESOURCE,
    date: getDefaultBookingDate(),
    slotStartTime: "",
    customerName: "",
    customerPhone: "",
    customerEmail: "",
    customerBirthDate: "",
    saveServicePreference: false,
    saveResourcePreference: false,
    notes: ""
  });
  const [formErrors, setFormErrors] = useState<BookingFormErrors>({});

  useEffect(() => {
    let ignore = false;

    setState({ status: "loading" });
    setSlotsState({ status: "idle" });
    setSubmitState({ status: "idle" });
    Promise.all([
      getPublicBookingContext(apiClient, tenantSlug),
      getPublicServices(apiClient, tenantSlug),
      getPublicResources(apiClient, tenantSlug)
    ])
      .then(([context, services, resources]) => {
        if (ignore) {
          return;
        }
        setState({ status: "success", context, services, resources });
        setForm((current) => ({
          ...current,
          serviceID: current.serviceID || services[0]?.id || "",
          resourceChoice: getInitialResourceChoice(context, resources)
        }));
      })
      .catch((error: unknown) => {
        if (ignore) {
          return;
        }
        if (error instanceof ApiClientError && error.status === 404) {
          setState({ status: "not_found" });
          return;
        }
        setState({
          status: "error",
          retryable: error instanceof ApiClientError ? error.retryable : true
        });
      });

    return () => {
      ignore = true;
    };
  }, [apiClient, tenantSlug]);

  const selectedService = state.status === "success"
    ? state.services.find((service) => service.id === form.serviceID)
    : undefined;
  const selectedSlot = slotsState.status === "success"
    ? slotsState.slots.find((slot) => slot.start_time === form.slotStartTime)
    : undefined;
  const selectedResource = state.status === "success" && form.resourceChoice !== ANY_RESOURCE
    ? state.resources.find((resource) => resource.id === form.resourceChoice)
    : undefined;
  const resourceSelectionMode = state.status === "success"
    ? state.context.booking_policy.booking_resource_selection_mode
    : "customer_choice_or_any";
  const needsResourceChoice = resourceSelectionMode === "customer_must_choose";
  const canUseGeneralAvailability = resourceSelectionMode !== "customer_must_choose";
  const canChooseResource = resourceSelectionMode !== "establishment_assigns";
  const customerCapturePolicy = state.status === "success"
    ? getCustomerCapturePolicy(state.context)
    : {
      requiresBirthDate: false,
      canSaveServicePreference: false,
      canSaveResourcePreference: false
    };
  const segmentPreset = state.status === "success"
    ? resolveBeautyBookingSegmentPreset(state.context)
    : beautyBookingSegmentPresets.barbearia;

  useEffect(() => {
    if (state.status !== "success" || !form.serviceID || !form.date) {
      setSlotsState({ status: "idle" });
      return;
    }

    if (needsResourceChoice && form.resourceChoice === ANY_RESOURCE) {
      setSlotsState({ status: "idle" });
      return;
    }

    let ignore = false;
    setSlotsState({ status: "loading" });
    setSubmitState((current) => current.status === "success" ? current : { status: "idle" });
    setForm((current) => ({ ...current, slotStartTime: "" }));

    const slotsRequest = form.resourceChoice === ANY_RESOURCE
      ? getPublicGeneralSlots(apiClient, state.context.tenant.slug, {
        serviceID: form.serviceID,
        date: form.date
      })
      : getPublicResourceSlots(apiClient, state.context.tenant.slug, {
        resourceID: form.resourceChoice,
        serviceID: form.serviceID,
        date: form.date
      });

    slotsRequest
      .then((response) => {
        if (!ignore) {
          setSlotsState({ status: "success", slots: response.slots });
        }
      })
      .catch((error: unknown) => {
        if (!ignore) {
          setSlotsState({
            status: "error",
            retryable: error instanceof ApiClientError ? error.retryable : true
          });
        }
      });

    return () => {
      ignore = true;
    };
  }, [apiClient, form.date, form.resourceChoice, form.serviceID, needsResourceChoice, slotsRetryKey, state]);

  if (state.status === "not_found") {
    return <NotFoundPage />;
  }

  if (state.status === "loading") {
    return (
      <BookingShell title="Preparando agenda online">
        <BookingStatus tone="neutral" title="Carregando agenda" text="Buscando servicos, profissionais e politicas do estabelecimento." />
      </BookingShell>
    );
  }

  if (state.status === "error") {
    return (
      <BookingShell
        title="Nao foi possivel carregar esta agenda"
        text={state.retryable ? "Tente novamente em instantes." : "Confira o link recebido pelo estabelecimento."}
      >
        <BookingStatus tone="danger" title="Agenda indisponivel" text="A conexao com a agenda publica falhou sem expor detalhes internos." />
      </BookingShell>
    );
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (state.status !== "success") {
      return;
    }

    const nextErrors = validatePublicBookingForm(form, {
      needsResourceChoice,
      requiresBirthDate: customerCapturePolicy.requiresBirthDate,
      hasServices: state.services.length > 0,
      hasResources: state.resources.length > 0,
      selectedSlot
    });
    setFormErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || !selectedSlot) {
      setSubmitState({ status: "idle" });
      return;
    }

    setSubmitState({ status: "submitting" });
    try {
      const appointment = await createPublicAppointment(
        apiClient,
        state.context.tenant.slug,
        buildCreatePublicAppointmentInput(form, selectedSlot, customerCapturePolicy)
      );
      setSubmitState({
        status: "success",
        serviceName: selectedService?.name ?? "Servico escolhido",
        resourceName: getSelectedResourceName(form.resourceChoice, selectedResource),
        startTime: appointment.appointment.start_time,
        endTime: appointment.appointment.end_time,
        timezone: appointment.appointment.timezone
      });
    } catch (error) {
      if (error instanceof ApiClientError && error.status === 409) {
        setSubmitState({ status: "conflict" });
        setSlotsState({ status: "idle" });
        setForm((current) => ({ ...current, slotStartTime: "" }));
        setStep(2);
        return;
      }
      setSubmitState({
        status: "error",
        retryable: error instanceof ApiClientError ? error.retryable : true
      });
    }
  }

  if (submitState.status === "success") {
    return (
      <BookingShell>
        <SuccessStep
          tenantName={state.context.tenant.name}
          customerName={form.customerName}
          state={submitState}
          onNewBooking={() => {
            setStep(0);
            setSubmitState({ status: "idle" });
            setForm((current) => ({
              ...current,
              slotStartTime: "",
              customerName: "",
              customerPhone: "",
              customerEmail: "",
              customerBirthDate: "",
              saveServicePreference: false,
              saveResourcePreference: false,
              notes: ""
            }));
          }}
        />
      </BookingShell>
    );
  }

  const canContinueFromService = Boolean(form.serviceID && state.services.length > 0);
  const canContinueFromResource = !needsResourceChoice || (form.resourceChoice !== ANY_RESOURCE && state.resources.length > 0);
  const canContinueFromSchedule = Boolean(form.date && form.slotStartTime);
  const canSubmit = form.customerName.trim().length >= 2
    && form.customerPhone.replace(/\D/g, "").length >= 8;

  return (
    <BookingShell>
      <form className="gb-booking-flow" onSubmit={handleSubmit} noValidate>
        <BookingTopBar
          step={step}
          breadcrumb={getBookingBreadcrumb(step, selectedService, selectedResource, form.resourceChoice)}
          onBack={step > 0
            ? () => {
              setSubmitState({ status: "idle" });
              setStep((current) => Math.max(0, current - 1) as BookingStep);
            }
            : undefined}
        />

        {step === 0 ? (
          <ServiceStep
            tenantName={state.context.tenant.name}
            preset={segmentPreset}
            services={state.services}
            selectedServiceID={form.serviceID}
            onSelect={(serviceID) => {
              setForm((current) => ({ ...current, serviceID, slotStartTime: "" }));
              setFormErrors((current) => ({ ...current, serviceID: undefined, slotStartTime: undefined }));
              setSubmitState({ status: "idle" });
            }}
          />
        ) : null}

        {step === 1 ? (
          <ResourceStep
            preset={segmentPreset}
            canUseGeneralAvailability={canUseGeneralAvailability}
            canChooseResource={canChooseResource}
            resources={state.resources}
            selectedResourceID={form.resourceChoice}
            needsResourceChoice={needsResourceChoice}
            onSelect={(resourceChoice) => {
              setForm((current) => ({ ...current, resourceChoice, slotStartTime: "" }));
              setFormErrors((current) => ({ ...current, resourceChoice: undefined, slotStartTime: undefined }));
              setSubmitState({ status: "idle" });
            }}
          />
        ) : null}

        {step === 2 ? (
          <ScheduleStep
            date={form.date}
            selectedSlotStartTime={form.slotStartTime}
            slotsState={slotsState}
            submitState={submitState}
            onRetrySlots={() => setSlotsRetryKey((current) => current + 1)}
            onSelectDate={(date) => {
              setForm((current) => ({ ...current, date, slotStartTime: "" }));
              setFormErrors((current) => ({ ...current, date: undefined, slotStartTime: undefined }));
              setSubmitState({ status: "idle" });
            }}
            onSelectSlot={(slot) => {
              setForm((current) => ({ ...current, slotStartTime: slot.start_time }));
              setFormErrors((current) => ({ ...current, slotStartTime: undefined }));
              setSubmitState({ status: "idle" });
            }}
            onChooseAnotherSlot={() => {
              setSubmitState({ status: "idle" });
              setSlotsRetryKey((current) => current + 1);
            }}
          />
        ) : null}

        {step === 3 ? (
          <CustomerStep
            preset={segmentPreset}
            form={form}
            formErrors={formErrors}
            selectedService={selectedService}
            selectedResourceName={getSelectedResourceName(form.resourceChoice, selectedResource)}
            selectedSlot={selectedSlot}
            customerCapturePolicy={customerCapturePolicy}
            submitState={submitState}
            onChange={(patch) => setForm((current) => ({ ...current, ...patch }))}
            onClearError={(field) => setFormErrors((current) => ({ ...current, [field]: undefined }))}
            onRetrySubmit={() => undefined}
            onChooseAnotherSlot={() => {
              setSubmitState({ status: "idle" });
              setStep(2);
              setSlotsRetryKey((current) => current + 1);
            }}
          />
        ) : null}

        {state.services.length === 0 ? (
          <BookingStatus tone="neutral" title="Agenda ainda sem servicos" text="Este estabelecimento ainda nao publicou servicos para agendamento online." />
        ) : null}

        {needsResourceChoice && state.resources.length === 0 ? (
          <BookingStatus tone="danger" title="Nenhum profissional disponivel" text="Esta agenda exige escolha de profissional, mas ainda nao ha profissionais disponiveis para o cliente." />
        ) : null}

        {formErrors.serviceID ? <FieldError>{formErrors.serviceID}</FieldError> : null}
        {formErrors.resourceChoice ? <FieldError>{formErrors.resourceChoice}</FieldError> : null}
        {formErrors.date ? <FieldError>{formErrors.date}</FieldError> : null}
        {formErrors.slotStartTime && step === 2 ? <FieldError>{formErrors.slotStartTime}</FieldError> : null}

        {step === 0 ? (
          <StickyAction title="Continuar" subtitle="~ 1 minuto para concluir" disabled={!canContinueFromService} onClick={() => setStep(1)} />
        ) : null}
        {step === 1 ? (
          <StickyAction title="Continuar" subtitle="Escolher data e horario" disabled={!canContinueFromResource} onClick={() => setStep(2)} />
        ) : null}
        {step === 2 ? (
          <StickyAction title="Avancar" subtitle="Preencher dados" disabled={!canContinueFromSchedule} onClick={() => setStep(3)} />
        ) : null}
        {step === 3 ? (
          <StickyAction title={getSubmitButtonLabel(submitState)} subtitle="Confirmar solicitacao" disabled={!canSubmit || submitState.status === "submitting"} loading={submitState.status === "submitting"} submit />
        ) : null}
      </form>
    </BookingShell>
  );
}

function ServiceStep({
  tenantName,
  preset,
  services,
  selectedServiceID,
  onSelect
}: {
  tenantName: string;
  preset: BookingSegmentPreset;
  services: PublicService[];
  selectedServiceID: string;
  onSelect: (serviceID: string) => void;
}) {
  return (
    <section className="gb-booking-step gb-booking-step--intro" aria-labelledby="booking-service-title">
      <div className="gb-booking-tenantHero">
        <div className="gb-booking-tenantHero__mark" aria-hidden="true">
          {getInitials(tenantName)}
        </div>
        <div>
          <h1 id="booking-service-title">{tenantName}</h1>
          <p>{preset.copy.serviceIntro}</p>
        </div>
      </div>
      <p className="gb-booking-tenantHero__note">
        Aqui você sai do jeito que sempre quis. Atendimento exclusivo, sem pressa e com os melhores produtos do mercado.
      </p>
      <div className="gb-booking-summary" aria-label="Resumo da agenda publica">
        <span aria-label="Previa visual de avaliacoes">★ 4.9 · Prévia de avaliações</span>
        <span aria-label="Local">São Paulo, SP</span>
        <span aria-label="Disponibilidade">Aberto agora</span>
      </div>
      <hr className="gb-booking-sep" />
      <p className="gb-booking-sectionLabel">Selecione o servico</p>
      <div className="gb-booking-options gb-booking-options--services">
        {services.map((service, index) => {
          const selected = selectedServiceID === service.id;
          const visual = getServiceVisual(service);
          return (
            <button
              type="button"
              className={`gb-booking-card gb-booking-service ${selected ? "selected" : ""}`}
              key={service.id}
              onClick={() => onSelect(service.id)}
              aria-pressed={selected}
            >
              <span className={`gb-booking-service__image gb-booking-service__image--${visual.tone}`} aria-hidden="true">
                <ServiceIcon visual={visual} />
              </span>
              <span className="gb-booking-card__body">
                <span className="gb-booking-card__row">
                  <strong>{service.name}</strong>
                  {index === 0 ? <em>Mais escolhido</em> : null}
                </span>
                {service.description ? <small>{service.description}</small> : null}
                <span className="gb-booking-card__meta">
                  <span>~{service.duration_minutes} min</span>
                  <b>{formatPrice(service.price_cents)}</b>
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function ResourceStep({
  preset,
  canUseGeneralAvailability,
  canChooseResource,
  resources,
  selectedResourceID,
  needsResourceChoice,
  onSelect
}: {
  preset: BookingSegmentPreset;
  canUseGeneralAvailability: boolean;
  canChooseResource: boolean;
  resources: PublicResource[];
  selectedResourceID: string;
  needsResourceChoice: boolean;
  onSelect: (resourceChoice: string) => void;
}) {
  return (
    <section className="gb-booking-step" aria-label="Profissional">
      <div className="gb-booking-stepHead">
        <h1>Quem vai atender?</h1>
        <p>Escolha um profissional ou deixe a equipe encontrar o melhor horario disponivel.</p>
      </div>
      <div className="gb-booking-options">
        {canUseGeneralAvailability ? (
          <button
            type="button"
            className={`gb-booking-card gb-booking-resource ${selectedResourceID === ANY_RESOURCE ? "selected" : ""}`}
            onClick={() => onSelect(ANY_RESOURCE)}
            aria-pressed={selectedResourceID === ANY_RESOURCE}
          >
            <span className="gb-booking-resource__avatar gb-booking-resource__avatar--any" aria-hidden="true">
              <ServiceIcon visual={{ key: "combo", label: "Equipe", tone: "blue" }} />
            </span>
              <span className="gb-booking-card__body">
                <strong>Sem preferencia</strong>
                <small>{preset.copy.anyProfessional}</small>
              </span>
          </button>
        ) : null}
        {canChooseResource ? resources.map((resource, index) => {
          const selected = selectedResourceID === resource.id;
          return (
            <button
              type="button"
              className={`gb-booking-card gb-booking-resource ${selected ? "selected" : ""}`}
              key={resource.id}
              onClick={() => onSelect(resource.id)}
              aria-pressed={selected}
            >
              <ResourceAvatar resource={resource} />
              <span className="gb-booking-card__body">
                <span className="gb-booking-card__row">
                  <strong>{resource.name}</strong>
                  <em>★ {index === 1 ? "4.8" : "4.9"}</em>
                </span>
                <small>{index === 0 ? "Barbeiro Sênior" : formatResourceType(resource.type)}</small>
                <span className="gb-booking-resource__bio">Foto e agenda publicadas pelo estabelecimento.</span>
              </span>
            </button>
          );
        }) : null}
      </div>
      {needsResourceChoice && resources.length === 0 ? (
        <BookingStatus tone="danger" title="Nenhum profissional disponivel" text="Esta agenda exige escolha de profissional, mas ainda nao ha profissionais disponiveis para o cliente." />
      ) : null}
    </section>
  );
}

function ScheduleStep({
  date,
  selectedSlotStartTime,
  slotsState,
  submitState,
  onRetrySlots,
  onSelectDate,
  onSelectSlot,
  onChooseAnotherSlot
}: {
  date: string;
  selectedSlotStartTime: string;
  slotsState: SlotsState;
  submitState: SubmitState;
  onRetrySlots: () => void;
  onSelectDate: (date: string) => void;
  onSelectSlot: (slot: PublicSlot) => void;
  onChooseAnotherSlot: () => void;
}) {
  return (
    <section className="gb-booking-step" aria-label="Data e Horario">
      <div className="gb-booking-stepHead gb-booking-stepHead--compact">
        <h1>Quando?</h1>
        <p>Escolha uma data e um horário livre.</p>
      </div>
      <BookingCalendar selectedDate={date} onSelectDate={onSelectDate} />
      <div className="gb-booking-slots-section">
        <h3>Horarios disponiveis</h3>
        <SlotPicker
          state={slotsState}
          selectedSlotStartTime={selectedSlotStartTime}
          onRetry={onRetrySlots}
          onSelect={onSelectSlot}
        />
      </div>
      {submitState.status === "conflict" ? (
        <BookingStatus
          tone="danger"
          title="Horario acabou de ficar indisponivel"
          text="Esse horario foi ocupado enquanto voce preenchia os dados. Busque a disponibilidade novamente e escolha outro horario livre."
          action={onChooseAnotherSlot}
          actionLabel="Escolher outro horario"
        />
      ) : null}
    </section>
  );
}

function CustomerStep({
  preset,
  form,
  formErrors,
  selectedService,
  selectedResourceName,
  selectedSlot,
  customerCapturePolicy,
  submitState,
  onChange,
  onClearError,
  onRetrySubmit,
  onChooseAnotherSlot
}: {
  preset: BookingSegmentPreset;
  form: BookingFormState;
  formErrors: BookingFormErrors;
  selectedService?: PublicService;
  selectedResourceName: string;
  selectedSlot?: PublicSlot;
  customerCapturePolicy: {
    requiresBirthDate: boolean;
    canSaveServicePreference: boolean;
    canSaveResourcePreference: boolean;
  };
  submitState: SubmitState;
  onChange: (patch: Partial<BookingFormState>) => void;
  onClearError: (field: keyof BookingFormState) => void;
  onRetrySubmit: () => void;
  onChooseAnotherSlot: () => void;
}) {
  return (
    <section className="gb-booking-step" aria-label="Seus Dados">
      <div className="gb-booking-review" aria-label="Resumo antes de confirmar">
        <div className="gb-booking-review__hero">
          <span aria-hidden="true"><ServiceIcon visual={getServiceVisual(selectedService ?? { name: "", description: "" })} /></span>
          <p>{selectedService?.name ?? "Servico escolhido"} · {selectedResourceName}</p>
          <strong>{selectedSlot ? formatDateTimeLabel(selectedSlot.start_time) : "Horario escolhido"}</strong>
        </div>
        <div><span>Duração estimada</span><strong>~{selectedService?.duration_minutes ?? 45} min</strong></div>
        <div><span>Total</span><strong>{selectedService ? formatPrice(selectedService.price_cents) : "-"}</strong></div>
      </div>

      <div className="gb-booking-fields">
        <label className="gb-booking-field">
          <span>Seu nome</span>
          <input
            type="text"
            value={form.customerName}
            autoComplete="name"
            placeholder="Nome completo"
            onChange={(event) => {
              onChange({ customerName: event.target.value });
              onClearError("customerName");
            }}
          />
        </label>
        <label className="gb-booking-field">
          <span>WhatsApp</span>
          <input
            type="tel"
            value={form.customerPhone}
            autoComplete="tel"
            inputMode="tel"
            placeholder="(11) 90000-0000"
            onChange={(event) => {
              onChange({ customerPhone: event.target.value });
              onClearError("customerPhone");
            }}
          />
        </label>
        <label className="gb-booking-field">
          <span>Email opcional</span>
          <input
            type="email"
            value={form.customerEmail}
            autoComplete="email"
            inputMode="email"
            placeholder="Receba confirmacao e lembretes por email"
            onChange={(event) => {
              onChange({ customerEmail: event.target.value });
              onClearError("customerEmail");
            }}
          />
        </label>
        {customerCapturePolicy.requiresBirthDate ? (
          <label className="gb-booking-field">
            <span>Data de nascimento</span>
            <input
              type="date"
              value={form.customerBirthDate}
              autoComplete="bday"
              max={getDefaultBookingDate()}
              onChange={(event) => {
                onChange({ customerBirthDate: event.target.value });
                onClearError("customerBirthDate");
              }}
            />
          </label>
        ) : null}
      </div>
      <p className="gb-booking-help">{preset.copy.customerHelp}</p>
      {formErrors.customerName ? <FieldError>{formErrors.customerName}</FieldError> : null}
      {formErrors.customerPhone ? <FieldError>{formErrors.customerPhone}</FieldError> : null}
      {formErrors.customerEmail ? <FieldError>{formErrors.customerEmail}</FieldError> : null}
      {formErrors.customerBirthDate ? <FieldError>{formErrors.customerBirthDate}</FieldError> : null}
      {customerCapturePolicy.canSaveServicePreference || (customerCapturePolicy.canSaveResourcePreference && form.resourceChoice !== ANY_RESOURCE) ? (
        <div className="gb-booking-preferences" aria-label="Preferencias para proximas visitas">
          <strong>Preferencias para proximas visitas</strong>
          <span>Opcional. Isto nao muda o servico nem o profissional deste agendamento.</span>
          {customerCapturePolicy.canSaveServicePreference && selectedService ? (
            <label className="gb-booking-check">
              <input
                type="checkbox"
                checked={form.saveServicePreference}
                onChange={(event) => onChange({ saveServicePreference: event.target.checked })}
              />
              <span>Salvar {selectedService.name} como servico de preferencia</span>
            </label>
          ) : null}
          {customerCapturePolicy.canSaveResourcePreference && form.resourceChoice !== ANY_RESOURCE ? (
            <label className="gb-booking-check">
              <input
                type="checkbox"
                checked={form.saveResourcePreference}
                onChange={(event) => onChange({ saveResourcePreference: event.target.checked })}
              />
              <span>Salvar este profissional como preferencia</span>
            </label>
          ) : null}
        </div>
      ) : null}
      <label className="gb-booking-field">
        <span>Observacao opcional</span>
        <textarea
          value={form.notes}
          rows={3}
          onChange={(event) => onChange({ notes: event.target.value })}
          placeholder={preset.copy.notesPlaceholder}
        />
      </label>
      <SubmitFeedback state={submitState} onRetry={onRetrySubmit} onChooseAnotherSlot={onChooseAnotherSlot} />
    </section>
  );
}

function BookingTopBar({ step, breadcrumb, onBack }: { step: BookingStep; breadcrumb: string; onBack?: () => void }) {
  const title = step === 0 ? "Agendamento online" : step === 1 ? "Profissional" : step === 2 ? "Data e Horario" : "Seus Dados";

  return (
    <header className={`gb-booking-topbar gb-booking-topbar--step-${step}`}>
      {onBack ? (
        <button type="button" onClick={onBack} aria-label="Voltar">{"<"}</button>
      ) : null}
      <div className="gb-booking-topbar__logo" aria-label="THH GO">
        <img src={brandMarkPath} alt="" aria-hidden="true" />
        <b>GO</b>
      </div>
      <div className="gb-booking-topbar__center">
        <span>{step === 0 ? "Agendamento online" : breadcrumb}</span>
        <div aria-hidden="true">
          {Array.from({ length: STEP_COUNT }, (_, index) => index + 1).map((item) => (
            <i key={item} className={item === step ? "active" : item < step ? "done" : ""} />
          ))}
        </div>
      </div>
      <small>{title}</small>
    </header>
  );
}

function BookingCalendar({ selectedDate, onSelectDate }: { selectedDate: string; onSelectDate: (date: string) => void }) {
  const initialDate = parseLocalDate(selectedDate || getDefaultBookingDate());
  const [visibleMonth, setVisibleMonth] = useState(() => new Date(initialDate.getFullYear(), initialDate.getMonth(), 1));
  const today = parseLocalDate(getDefaultBookingDate());
  const isCurrentMonth = visibleMonth.getFullYear() === today.getFullYear() && visibleMonth.getMonth() === today.getMonth();
  const monthName = new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" }).format(visibleMonth);
  const firstDay = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
  const lastDay = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0);
  const leadingBlanks = firstDay.getDay();
  const cells = [
    ...Array.from({ length: leadingBlanks }).map((_, index) => ({ type: "blank" as const, key: `blank-${index}` })),
    ...Array.from({ length: lastDay.getDate() }).map((_, index) => {
    const date = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), index + 1);
    const value = formatDateInputValue(date);
    return {
      type: "day" as const,
      key: value,
      date,
      value,
      day: date.getDate(),
      isSelected: value === selectedDate,
      isPast: date < today
    };
    })
  ];

  return (
    <div className="gb-booking-calendar">
      <div className="gb-booking-calendar__head">
        <h3>{capitalize(monthName)}</h3>
        <div>
          <button
            type="button"
            aria-label="Mes anterior"
            disabled={isCurrentMonth}
            onClick={() => setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))}
          >
            {"<"}
          </button>
          <button
            type="button"
            aria-label="Proximo mes"
            onClick={() => setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))}
          >
            {">"}
          </button>
        </div>
      </div>
      <div className="gb-booking-calendar__grid" aria-label="Datas disponiveis">
        {["D", "S", "T", "Q", "Q", "S", "S"].map((weekday) => <span key={weekday}>{weekday}</span>)}
        {cells.map((cell) => cell.type === "blank" ? (
          <i key={cell.key} aria-hidden="true" />
        ) : (
          <button
            key={cell.key}
            type="button"
            className={cell.isSelected ? "selected" : ""}
            disabled={cell.isPast}
            onClick={() => onSelectDate(cell.value)}
            aria-pressed={cell.isSelected}
          >
            {cell.day}
          </button>
        ))}
      </div>
    </div>
  );
}

function ResourceAvatar({ resource }: { resource: PublicResource }) {
  if (resource.avatar_url) {
    return <img className="gb-booking-resource__avatar" src={resource.avatar_url} alt="" />;
  }
  return <span className="gb-booking-resource__avatar" aria-hidden="true">{getInitials(resource.name)}</span>;
}

function ServiceIcon({ visual }: { visual: ServiceVisual }) {
  const common = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" } as const;
  const icons: Record<ServiceVisual["key"], ReactNode> = {
    cut: <><path {...common} d="M6 3l6 6M6 21l6-6M15 12l5-5" /><circle {...common} cx="18" cy="6" r="2" /><circle {...common} cx="18" cy="18" r="2" /></>,
    beard: <><path {...common} d="M5 8c2-1.4 4.3-2 7-2s5 .6 7 2v5.5c0 3.6-3.1 6.5-7 6.5s-7-2.9-7-6.5V8Z" /><path {...common} d="M9 12c.8.7 1.8 1 3 1s2.2-.3 3-1" /></>,
    combo: <><path {...common} d="m12 3 2.7 5.5 6.1.9-4.4 4.3 1 6-5.4-2.8-5.4 2.8 1-6-4.4-4.3 6.1-.9L12 3Z" /></>,
    color: <><path {...common} d="M12 3s5 5.4 5 10a5 5 0 0 1-10 0c0-4.6 5-10 5-10Z" /><path {...common} d="M9.5 14h5" /></>,
    nails: <><path {...common} d="M8 21h8l1-11a5 5 0 0 0-10 0l1 11Z" /><path {...common} d="M9 12h6" /></>,
    brow: <><path {...common} d="M4 13c4-5 12-5 16 0" /><path {...common} d="M7 15c3-2 7-2 10 0" /></>,
    makeup: <><path {...common} d="M7 20 19 8l-3-3L4 17v3h3Z" /><path {...common} d="m14 7 3 3" /></>,
    esthetic: <><circle {...common} cx="12" cy="12" r="7" /><path {...common} d="M12 8v8M8 12h8" /></>,
    default: <><rect {...common} x="5" y="5" width="14" height="14" rx="4" /><path {...common} d="M9 12h6" /></>
  };

  return (
    <svg viewBox="0 0 24 24" aria-label={visual.label} role="img">
      {icons[visual.key]}
    </svg>
  );
}

function StickyAction({
  title,
  subtitle,
  disabled,
  loading = false,
  submit = false,
  onClick
}: {
  title: string;
  subtitle?: string;
  disabled?: boolean;
  loading?: boolean;
  submit?: boolean;
  onClick?: () => void;
}) {
  return (
    <div className="gb-booking-sticky">
      <button type={submit ? "submit" : "button"} disabled={disabled || loading} onClick={onClick}>
        <span>
          <strong>{title}</strong>
          {subtitle ? <small>{subtitle}</small> : null}
        </span>
        <b aria-hidden="true">{loading ? "..." : ">"}</b>
      </button>
    </div>
  );
}

function SuccessStep({
  tenantName,
  customerName,
  state,
  onNewBooking
}: {
  tenantName: string;
  customerName: string;
  state: Extract<SubmitState, { status: "success" }>;
  onNewBooking: () => void;
}) {
  const firstName = customerName.trim().split(/\s+/)[0] || "tudo certo";

  return (
    <section className="gb-booking-success" role="status" aria-label="Solicitacao registrada">
      <div className="gb-booking-brand" aria-label="THH">
        <img src={brandMarkPath} alt="" aria-hidden="true" />
      </div>
      <div className="gb-booking-success__icon" aria-hidden="true">OK</div>
      <h1>Tudo certo, {firstName}</h1>
      <p>
        Seu horario foi solicitado em <strong>{tenantName}</strong> com <strong>{state.resourceName}</strong> para <strong>{formatDateTimeLabel(state.startTime)}</strong>.
      </p>
      <dl className="gb-booking-success__summary" aria-label="Resumo da solicitacao">
        <div><dt>Servico</dt><dd>{state.serviceName}</dd></div>
        <div><dt>Horario</dt><dd>{formatTime(state.startTime)} - {formatTime(state.endTime)}</dd></div>
        <div><dt>Proximo passo</dt><dd>Acompanhe a resposta pelo canal informado.</dd></div>
      </dl>
      <button type="button" className="gb-booking-success__primary" onClick={() => downloadCalendarFile(tenantName, state)}>Adicionar ao calendario</button>
      <button type="button" className="gb-booking-success__secondary" onClick={onNewBooking}>Fazer novo agendamento</button>
    </section>
  );
}

export function validatePublicBookingForm(
  form: BookingFormState,
  options: {
    needsResourceChoice: boolean;
    requiresBirthDate: boolean;
    hasServices: boolean;
    hasResources: boolean;
    selectedSlot?: PublicSlot;
  }
): BookingFormErrors {
  const errors: BookingFormErrors = {};
  if (!options.hasServices || !form.serviceID) {
    errors.serviceID = "Escolha um servico publicado.";
  }
  if (options.needsResourceChoice && (!options.hasResources || form.resourceChoice === ANY_RESOURCE)) {
    errors.resourceChoice = "Escolha um profissional para continuar.";
  }
  if (!form.date) {
    errors.date = "Escolha uma data.";
  }
  if (!form.slotStartTime || !options.selectedSlot) {
    errors.slotStartTime = "Escolha um horario disponivel.";
  }
  if (form.customerName.trim().length < 2) {
    errors.customerName = "Informe seu nome.";
  }
  if (form.customerPhone.replace(/\D/g, "").length < 8) {
    errors.customerPhone = "Informe um celular valido.";
  }
  if (form.customerEmail.trim() && !isValidEmail(form.customerEmail.trim())) {
    errors.customerEmail = "Informe um email valido ou deixe em branco.";
  }
  if (options.requiresBirthDate && !form.customerBirthDate) {
    errors.customerBirthDate = "Informe sua data de nascimento.";
  }
  return errors;
}

export function buildCreatePublicAppointmentInput(
  form: BookingFormState,
  selectedSlot: PublicSlot,
  policy: {
    requiresBirthDate: boolean;
    canSaveServicePreference: boolean;
    canSaveResourcePreference: boolean;
  } = {
    requiresBirthDate: false,
    canSaveServicePreference: false,
    canSaveResourcePreference: false
  }
): CreatePublicAppointmentInput {
  const input: CreatePublicAppointmentInput = {
    service_id: form.serviceID,
    start_time: selectedSlot.start_time,
    customer_name: form.customerName.trim(),
    customer_phone: form.customerPhone.trim()
  };

  if (form.resourceChoice !== ANY_RESOURCE) {
    input.resource_id = form.resourceChoice;
  }
  if (form.customerEmail.trim()) {
    input.customer_email = form.customerEmail.trim();
  }
  if (policy.requiresBirthDate && form.customerBirthDate) {
    input.customer_birth_date = form.customerBirthDate;
  }
  if (policy.canSaveServicePreference && form.saveServicePreference) {
    input.preferred_service_id = form.serviceID;
  }
  if (policy.canSaveResourcePreference && form.saveResourcePreference && form.resourceChoice !== ANY_RESOURCE) {
    input.preferred_resource_id = form.resourceChoice;
  }
  if (form.notes.trim()) {
    input.notes = form.notes.trim();
  }

  return input;
}

function SlotPicker({
  state,
  selectedSlotStartTime,
  onRetry,
  onSelect
}: {
  state: SlotsState;
  selectedSlotStartTime: string;
  onRetry: () => void;
  onSelect: (slot: PublicSlot) => void;
}) {
  if (state.status === "idle") {
    return <BookingStatus tone="neutral" title="Horarios aguardando escolhas" text="Escolha servico, profissional e data para consultar a disponibilidade." action={onRetry} actionLabel="Buscar horarios" />;
  }
  if (state.status === "loading") {
    return <BookingStatus tone="neutral" title="Buscando horarios" text="Conferindo a agenda publica em tempo real." />;
  }
  if (state.status === "error") {
    return (
      <BookingStatus
        tone="danger"
        title="Nao foi possivel buscar horarios"
        text={state.retryable ? "Tente mudar a data ou consultar novamente em instantes." : "Confira as escolhas acima."}
        action={state.retryable ? onRetry : undefined}
        actionLabel="Tentar novamente"
      />
    );
  }
  if (state.slots.length === 0) {
    return <BookingStatus tone="neutral" title="Sem horarios neste dia" text="Tente outra data ou outro profissional disponivel. A agenda mostra apenas horarios confirmados como livres." />;
  }

  return (
    <div className="gb-booking-slots" role="radiogroup" aria-label="Horarios disponiveis">
      {state.slots.map((slot) => (
        <button
          type="button"
          key={slot.start_time}
          className={slot.start_time === selectedSlotStartTime ? "selected" : ""}
          onClick={() => onSelect(slot)}
          aria-pressed={slot.start_time === selectedSlotStartTime}
        >
          <strong>{formatTime(slot.start_time)}</strong>
          <span>{formatTime(slot.end_time)}</span>
        </button>
      ))}
    </div>
  );
}

function SubmitFeedback({
  state,
  onRetry,
  onChooseAnotherSlot
}: {
  state: SubmitState;
  onRetry: () => void;
  onChooseAnotherSlot: () => void;
}) {
  if (state.status === "success") {
    return (
      <div className="gb-booking-status gb-booking-status--final success" role="status">
        <strong>Solicitacao registrada</strong>
        <span>O estabelecimento recebeu seu pedido de agendamento. A solicitacao ainda depende da disponibilidade real da agenda.</span>
        <dl className="gb-booking-final-summary" aria-label="Resumo da solicitacao">
          <div>
            <dt>Servico</dt>
            <dd>{state.serviceName}</dd>
          </div>
          <div>
            <dt>Horario</dt>
            <dd>{formatTime(state.startTime)} - {formatTime(state.endTime)}</dd>
          </div>
          <div>
            <dt>Fuso</dt>
            <dd>{formatTimezoneLabel(state.timezone)}</dd>
          </div>
        </dl>
        <p className="gb-booking-next-step">Proximo passo: acompanhe a resposta pelo canal informado ao estabelecimento.</p>
      </div>
    );
  }
  if (state.status === "conflict") {
    return (
      <BookingStatus
        tone="danger"
        title="Horario acabou de ficar indisponivel"
        text="Esse horario foi ocupado enquanto voce preenchia os dados. Busque a disponibilidade novamente e escolha outro horario livre."
        action={onChooseAnotherSlot}
        actionLabel="Escolher outro horario"
      />
    );
  }
  if (state.status === "error") {
    return (
      <BookingStatus
        tone="danger"
        title="Nao foi possivel criar o agendamento"
        text={state.retryable ? "A solicitacao nao foi registrada. Tente novamente em instantes." : "Revise os dados e escolha outro horario."}
        action={state.retryable ? onRetry : undefined}
        actionLabel="Tentar novamente"
        actionType="submit"
      />
    );
  }
  return null;
}

function BookingShell({
  title,
  text = "Carregando o perfil publico do estabelecimento.",
  children
}: {
  title?: string;
  text?: string;
  children?: ReactNode;
}) {
  return (
    <section className="gb-booking-page" aria-labelledby="booking-title">
      <div className="gb-booking-shell">
        {title ? (
          <>
            <p className="gb-eyebrow">Agendamento online</p>
            <h1 id="booking-title">{title}</h1>
            <p>{text}</p>
            <p className="gb-booking-link">{formatPublicBookingLinkLabel()}</p>
          </>
        ) : null}
        {children}
      </div>
    </section>
  );
}

function formatPublicBookingLinkLabel(): string {
  return "Agenda publica";
}

function formatTimezoneLabel(timezone: string): string {
  return timezone ? "Horario local do estabelecimento" : "Horario local";
}

function BookingStatus({
  tone,
  title,
  text,
  action,
  actionLabel,
  actionType = "button"
}: {
  tone: "neutral" | "success" | "danger";
  title: string;
  text: string;
  action?: () => void;
  actionLabel?: string;
  actionType?: "button" | "submit";
}) {
  return (
    <div className={`gb-booking-status ${tone}`} role={tone === "danger" ? "alert" : "status"}>
      <strong>{title}</strong>
      <span>{text}</span>
      {action && actionLabel ? (
        <button type={actionType} className="gb-booking-status__action" onClick={action}>
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

function getSubmitButtonLabel(state: SubmitState): string {
  if (state.status === "submitting") {
    return "Agendando...";
  }
  if (state.status === "success") {
    return "Solicitacao registrada";
  }
  return "Confirmar agendamento";
}

function getBookingBreadcrumb(step: BookingStep, service?: PublicService, resource?: PublicResource, resourceChoice?: string): string {
  if (step === 1) {
    return service ? `${service.name} · ${formatPrice(service.price_cents)}` : "Escolha o profissional";
  }
  if (step === 2) {
    return `${getSelectedResourceName(resourceChoice ?? ANY_RESOURCE, resource)} · ${service?.name ?? "Servico"}`;
  }
  if (step === 3) {
    return "Quase la — confirme seus dados";
  }
  return "Agendamento online";
}

function FieldError({ children }: { children: ReactNode }) {
  return <p className="gb-booking-error" role="alert">{children}</p>;
}

function getInitialResourceChoice(context: PublicBookingContext, resources: PublicResource[]): string {
  if (context.booking_policy.booking_resource_selection_mode === "customer_must_choose") {
    return resources[0]?.id || ANY_RESOURCE;
  }
  return ANY_RESOURCE;
}

function getCustomerCapturePolicy(context: PublicBookingContext): {
  requiresBirthDate: boolean;
  canSaveServicePreference: boolean;
  canSaveResourcePreference: boolean;
} {
  const fields = context.booking_policy.extended_customer_fields;
  const isExtended = context.booking_policy.customer_capture_mode === "extended";

  return {
    requiresBirthDate: isExtended && fields.includes(EXTENDED_FIELD_BIRTH_DATE),
    canSaveServicePreference: isExtended && fields.includes(EXTENDED_FIELD_PREFERRED_SERVICE_ID),
    canSaveResourcePreference: isExtended && fields.includes(EXTENDED_FIELD_PREFERRED_RESOURCE_ID)
  };
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function getDefaultBookingDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function parseLocalDate(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) {
    return new Date();
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function formatDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatDateTimeLabel(value: string): string {
  const date = parseLocalDate(value);
  const dateLabel = new Intl.DateTimeFormat("pt-BR", {
    weekday: "long",
    day: "numeric",
    month: "long"
  }).format(date);

  return `${dateLabel}, as ${formatTime(value)}`;
}

function getInitials(value: string): string {
  const initials = value
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  return initials || "TH";
}

function getSelectedResourceName(resourceChoice: string, resource?: PublicResource): string {
  if (resourceChoice === ANY_RESOURCE) {
    return "Equipe THH";
  }
  return resource?.name ?? "Profissional escolhido";
}

export function getServiceVisual(service: Pick<PublicService, "name" | "description">): ServiceVisual {
  const text = normalizeServiceText(`${service.name} ${service.description ?? ""}`);
  if (matchesService(text, ["corte", "haircut", "tesoura", "fade", "degrade", "degradê"])) {
    return { key: "cut", label: "Corte", tone: "blue" };
  }
  if (matchesService(text, ["barba", "barbear", "barber", "navalha"])) {
    return { key: "beard", label: "Barba", tone: "gray" };
  }
  if (matchesService(text, ["combo", "pacote", "experiencia", "experiência"])) {
    return { key: "combo", label: "Combo", tone: "amber" };
  }
  if (matchesService(text, ["cor", "coloracao", "coloração", "platinum", "platinado", "mecha", "luzes"])) {
    return { key: "color", label: "Coloracao", tone: "purple" };
  }
  if (matchesService(text, ["unha", "nail", "manicure", "pedicure", "gel"])) {
    return { key: "nails", label: "Unhas", tone: "rose" };
  }
  if (matchesService(text, ["sobrancelha", "brow", "henna"])) {
    return { key: "brow", label: "Sobrancelha", tone: "green" };
  }
  if (matchesService(text, ["maquiagem", "make", "makeup"])) {
    return { key: "makeup", label: "Maquiagem", tone: "purple" };
  }
  if (matchesService(text, ["estetica", "estética", "limpeza", "facial", "massagem"])) {
    return { key: "esthetic", label: "Estetica", tone: "green" };
  }

  const fallbackTones: ServiceVisual["tone"][] = ["blue", "gray", "amber", "green", "purple", "rose"];
  const hash = Array.from(text || service.name).reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return { key: "default", label: "Servico", tone: fallbackTones[hash % fallbackTones.length] };
}

function normalizeServiceText(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function matchesService(value: string, terms: string[]): boolean {
  return terms.some((term) => value.includes(normalizeServiceText(term)));
}

function downloadCalendarFile(tenantName: string, state: Extract<SubmitState, { status: "success" }>) {
  const content = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//THH GO//Public Booking//PT-BR",
    "BEGIN:VEVENT",
    `UID:${state.startTime}-${state.serviceName}@thh-go`,
    `DTSTAMP:${formatCalendarTimestamp(new Date().toISOString())}`,
    `DTSTART:${formatCalendarTimestamp(state.startTime)}`,
    `DTEND:${formatCalendarTimestamp(state.endTime)}`,
    `SUMMARY:${escapeCalendarText(`${state.serviceName} em ${tenantName}`)}`,
    `DESCRIPTION:${escapeCalendarText(`Solicitacao de agendamento com ${state.resourceName}. Acompanhe a resposta pelo canal informado.`)}`,
    "END:VEVENT",
    "END:VCALENDAR"
  ].join("\r\n");
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "agendamento-thh.ics";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function formatCalendarTimestamp(value: string): string {
  return new Date(value).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
}

function escapeCalendarText(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/,/g, "\\,").replace(/;/g, "\\;").replace(/\n/g, "\\n");
}

function formatResourceType(value: string): string {
  if (value === "person") {
    return "Profissional";
  }
  return value;
}

function formatPrice(priceCents: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(priceCents / 100);
}

function formatTime(value: string): string {
  const localTime = /T(\d{2}:\d{2})/.exec(value)?.[1];
  if (localTime) {
    return localTime;
  }

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));
}
